import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhotoUpload } from "@/components/PhotoUpload";
import { Loader2, Save, KeyRound, UserCircle } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [photo, setPhoto] = useState(user?.photo || "");
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState("");

  const saveProfile = async () => {
    setSaving("profile");
    try {
      const r = await api.put("/auth/profile", { name, photo });
      setUser(r.data);
      toast.success("Profil diperbarui ✅");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  const changePw = async () => {
    if (pw.new_password.length < 6) return toast.error("Sandi baru minimal 6 karakter");
    if (pw.new_password !== pw.confirm) return toast.error("Konfirmasi sandi tidak sama");
    setSaving("pw");
    try {
      await api.post("/auth/change-password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Kata sandi berhasil diganti 🔒");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(""); }
  };

  return (
    <div className="space-y-6 animate-float-in max-w-2xl">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Profil Akun</h1>
        <p className="text-slate-500 mt-1">Kelola nama, foto profil, dan kata sandi Anda</p>
      </div>

      <Card className="p-6 rounded-2xl border-amber-100" data-testid="profile-card">
        <div className="flex items-center gap-2 mb-5"><UserCircle className="w-5 h-5 text-orange-500" /><h3 className="font-display text-xl font-bold">Data Profil</h3></div>
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <PhotoUpload value={photo} onChange={setPhoto} fallback={(name || "?").charAt(0)} size="w-28 h-28" rounded="rounded-full" testId="profile-photo-upload" />
          <div className="flex-1 w-full space-y-4">
            <div><Label>Nama</Label><Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1 rounded-xl" /></div>
            <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1 rounded-xl bg-slate-50" /></div>
            <div><Label>Peran</Label><Input value={user?.role === "admin" ? "Admin / Pengajar" : "Wali Murid"} disabled className="mt-1 rounded-xl bg-slate-50" /></div>
            <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving === "profile"} className="rounded-xl bg-orange-500 hover:bg-orange-600">
              {saving === "profile" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Simpan Profil</>}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-amber-100" data-testid="password-card">
        <div className="flex items-center gap-2 mb-5"><KeyRound className="w-5 h-5 text-sky-500" /><h3 className="font-display text-xl font-bold">Ganti Kata Sandi</h3></div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Sandi Lama</Label><Input data-testid="old-password-input" type="password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>Sandi Baru</Label><Input data-testid="new-password-input" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1 rounded-xl" /></div>
          <div><Label>Ulangi Sandi Baru</Label><Input data-testid="confirm-password-input" type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} className="mt-1 rounded-xl" /></div>
        </div>
        <Button data-testid="change-password-btn" onClick={changePw} disabled={saving === "pw"} className="rounded-xl bg-sky-600 hover:bg-sky-700 mt-4">
          {saving === "pw" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ganti Sandi"}
        </Button>
      </Card>
    </div>
  );
}
